import React, { useState, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createConversation } from '@/services/messageService';
import { Textarea } from '@/components/ui/textarea';
import { SmilePlus } from 'lucide-react';
import { EmojiClickData } from 'emoji-picker-react';

interface Family {
  id: string;
  name: string;
}

interface NewConversationDialogProps {
  children?: React.ReactNode;
  onSuccess?: (conversationId: string) => void;
}

const NewConversationDialog: React.FC<NewConversationDialogProps> = ({ 
  children, 
  onSuccess 
}) => {
  const [open, setOpen] = useState(false);
  const [familyId, setFamilyId] = useState<string>('');
  const [conversationTitle, setConversationTitle] = useState('');
  const [initialMessage, setInitialMessage] = useState('');
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // Get organization ID synchronously
  const [organizationId, setOrganizationId] = useState<string>('');
  
  // Get user's organization ID when component mounts
  React.useEffect(() => {
    if (user?.user_metadata?.organization_id) {
      setOrganizationId(user.user_metadata.organization_id);
    }
  }, [user]);

  // Fetch families
  const { data: families = [], isLoading } = useQuery({
    queryKey: ['families-for-messaging'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('families')
        .select('id, name')
        .order('name');
      if (error) throw error;
      return data as Family[];
    },
    enabled: !!user,
  });

  // Helper to validate UUID
  const isValidUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

  // Create new conversation with initial message
  const createConversationMutation = useMutation({
    mutationFn: async () => {
      if (!familyId || !organizationId) {
        throw new Error('Family ID and Organization ID are required');
      }
      if (!isValidUUID(familyId) || !isValidUUID(organizationId)) {
        throw new Error('Invalid family or organization ID.');
      }
      const title = conversationTitle.trim() || `Conversation with ${families.find(f => f.id === familyId)?.name || 'Family'}`;
      const newConversation = await createConversation(
        organizationId,
        familyId, 
        title
      );
      // If there's an initial message, send it right away
      if (initialMessage.trim() && newConversation?.id && user?.id) {
        await supabase
          .from('messages')
          .insert({
            conversation_id: newConversation.id,
            sender_id: user.id,
            sender_type: 'staff',
            content: initialMessage.trim(),
            organization_id: organizationId
          });
      }
      return newConversation;
    },
    onSuccess: (data) => {
      toast({
        title: 'Conversation started',
        description: initialMessage.trim() 
          ? 'Your message has been sent.' 
          : 'Your new conversation has been created.',
      });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      
      // Check if we're in admin or parent-portal context
      const isAdminRoute = window.location.pathname.includes('/admin');
      const route = isAdminRoute 
        ? `/admin/messages/${data.id}` 
        : `/messages/${data.id}`;
      
      // If onSuccess callback is provided, use it instead of navigation
      if (onSuccess) {
        onSuccess(data.id);
      } else {
        navigate(route);
      }
      
      setOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      console.error('Error creating conversation:', error);
      toast({
        title: 'Error creating conversation',
        description: error.message || 'The conversation could not be created. Please try again.',
        variant: 'destructive',
      });
    }
  });

  const resetForm = () => {
    setFamilyId('');
    setConversationTitle('');
    setInitialMessage('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!familyId) {
      toast({
        title: 'Family required',
        description: 'Please select a family for this conversation.',
        variant: 'destructive',
      });
      return;
    }
    
    if (!organizationId) {
      toast({
        title: 'Organization required',
        description: 'Your organization ID could not be determined.',
        variant: 'destructive',
      });
      return;
    }
    
    createConversationMutation.mutate();
  };

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const emoji = emojiData.emoji;
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const before = initialMessage.substring(0, start);
      const after = initialMessage.substring(end);
      const updated = before + emoji + after;
      setInitialMessage(updated);
      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
      }, 10);
    } else {
      setInitialMessage(initialMessage + emoji);
    }
    setShowEmojiPicker(false);
  };

  const EmojiPicker = React.lazy(() => import('emoji-picker-react'));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || <Button>New Conversation</Button>}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>New Conversation</DialogTitle>
            <DialogDescription>
              Start a new conversation with a family.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="family" className="text-right">
                Family
              </Label>
              <Select
                value={familyId}
                onValueChange={setFamilyId}
                disabled={isLoading}
              >
                <SelectTrigger id="family" className="col-span-3">
                  <SelectValue placeholder="Select a family" />
                </SelectTrigger>
                <SelectContent>
                  {families.map((family) => (
                    <SelectItem key={family.id} value={family.id}>
                      {family.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="title" className="text-right">
                Title (Optional)
              </Label>
              <Input
                id="title"
                value={conversationTitle}
                onChange={(e) => setConversationTitle(e.target.value)}
                placeholder="Conversation title"
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <Label htmlFor="message" className="text-right pt-2">
                Message
              </Label>
              <div className="col-span-3 min-h-[100px] relative">
                <Textarea
                  id="message"
                  ref={textareaRef}
                  value={initialMessage}
                  onChange={(e) => setInitialMessage(e.target.value)}
                  placeholder="Type your first message..."
                  className="min-h-[100px] pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-2"
                  title="Add emoji"
                  aria-label="Add emoji"
                  onClick={() => setShowEmojiPicker((v) => !v)}
                >
                  <SmilePlus className="h-5 w-5" />
                </Button>
                {showEmojiPicker && (
                  <div className="absolute right-0 z-50 mt-2">
                    <Suspense fallback={<div style={{ width: 300, height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading…</div>}>
                      <EmojiPicker onEmojiClick={handleEmojiClick} width={300} height={400} />
                    </Suspense>
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button 
              type="submit" 
              disabled={!familyId || createConversationMutation.isPending}
            >
              {createConversationMutation.isPending ? 'Creating...' : initialMessage.trim() ? 'Send Message' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NewConversationDialog;
